import { useState, useEffect } from 'react';
import { alphalendWebSocketService } from '../services/alphalendWebsocket';
export const useAlphalendSubscription = (room) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(!!room); // Only loading if room exists
    const [error, setError] = useState(null);
    useEffect(() => {
        // If no room, don't subscribe and clear data
        if (!room) {
            setData(null);
            setIsLoading(false);
            setError(null);
            return;
        }
        // Clear any previous errors
        setError(null);
        // Start with loading state - will be set to false when we get any data (cached or fresh)
        setIsLoading(true);
        // Subscribe for updates (both cached and fresh)
        const callback = (newData) => {
            try {
                setData(newData);
                setIsLoading(false); // Hide loading when any data arrives (cached or fresh)
                setError(null); // Clear any previous errors
            }
            catch (err) {
                console.error(`Error processing data for room ${room}:`, err);
                setError(`Failed to process data for ${room}`);
                setIsLoading(false);
            }
        };
        alphalendWebSocketService.subscribe(room, callback);
        // Additional debug for market_details
        if (room.startsWith('market_details:')) {
            console.log('🎯 This is a market_details subscription, should trigger special handling');
        }
        return () => {
            alphalendWebSocketService.unsubscribe(room, callback);
        };
    }, [room]);
    useEffect(() => {
        const unsubscribe = alphalendWebSocketService.onConnectionStatusChange(() => {
            const connectionStatus = alphalendWebSocketService.getConnectionStatus();
            switch (connectionStatus) {
                case 'connecting':
                case 'reconnecting':
                    // Show loading when connecting/reconnecting (but only if we already have data to show)
                    if (data) {
                        setIsLoading(true);
                    }
                    break;
                case 'connected':
                    // Don't immediately hide loading - let fresh data arrival hide it
                    // This prevents flicker between "connecting" and "fresh data"
                    break;
                case 'disconnected':
                    // Keep showing cached data without loading spinner
                    setIsLoading(false);
                    break;
                case 'failed':
                    // Show error state but keep cached data
                    setError('Connection failed');
                    setIsLoading(false);
                    break;
            }
        });
        return unsubscribe;
    }, [data]);
    // Re-read from cache when tab becomes visible to avoid empty UI during reconnect
    useEffect(() => {
        if (!room)
            return;
        const handleVisibilityChange = () => {
            var _a;
            if (document.visibilityState === 'visible') {
                const cached = alphalendWebSocketService.getCachedData(room);
                if ((_a = cached === null || cached === void 0 ? void 0 : cached.message) === null || _a === void 0 ? void 0 : _a.data) {
                    setData(cached.message.data);
                    setIsLoading(false);
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [room]);
    return {
        data,
        isLoading,
        error,
    };
};
